import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { rectSortingStrategy, SortableContext } from "@dnd-kit/sortable";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { useMemo } from "react";
import ProductCard from "@/components/products/ProductCard";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import type { Product } from "@/interfaces/product";
import SortableProductCard from "./SortableProductCard";

const PAGE_SIZE = 24;
// Without an activation distance dnd-kit's PointerSensor starts on pointerdown and swallows the
// click that toggles selection; a press moving less than this is a click, not a drag.
const DRAG_ACTIVATION_DISTANCE_PX = 8;
const NO_ADDED_IDS: ReadonlySet<string> = new Set();

type Props = {
  isLoading: boolean;
  isEditMode: boolean;
  isProUnlocked: boolean;
  displayedProducts: Product[];
  visibleEditGifts: Product[];
  editGifts: Product[];
  selectedIds: Set<string>;
  onProductClick: (id: string) => void;
  onToggleSelect: (id: string) => void;
  onRemoveCard: (id: string) => void;
  onDragEnd: (event: DragEndEvent) => void;
  addedIds?: ReadonlySet<string>;
};

export default function CollectionPreviewProductGrid({
  isLoading,
  isEditMode,
  isProUnlocked,
  displayedProducts,
  visibleEditGifts,
  editGifts,
  selectedIds,
  onProductClick,
  onToggleSelect,
  onRemoveCard,
  onDragEnd,
  addedIds = NO_ADDED_IDS,
}: Props) {
  // O(1) position lookup instead of O(n) findIndex inside the map
  const positionMap = useMemo(() => new Map(editGifts.map((p, i) => [p.id, i + 1])), [editGifts]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX } }),
    useSensor(KeyboardSensor),
  );

  const {
    renderedItems: renderedEditGifts,
    hasMore: editHasMore,
    sentinelRef: editSentinelRef,
  } = useInfiniteScroll(visibleEditGifts, PAGE_SIZE);

  const {
    renderedItems: renderedDisplayProducts,
    hasMore: viewHasMore,
    sentinelRef: viewSentinelRef,
  } = useInfiniteScroll(displayedProducts, PAGE_SIZE);

  return (
    <Box sx={{ px: "20px", pb: "20px" }}>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress
            aria-label="Loading gifts"
            size={32}
            sx={{ color: "var(--color-dark-dust)" }}
          />
        </Box>
      ) : isEditMode && isProUnlocked ? (
        <>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext
              items={visibleEditGifts.map((p) => p.id)}
              strategy={rectSortingStrategy}
            >
              <div className="grid grid-cols-3 gap-6 pt-4">
                {renderedEditGifts.map((product) => (
                  <SortableProductCard
                    key={product.id}
                    product={product}
                    onClick={onProductClick}
                    isSelected={selectedIds.has(product.id)}
                    onToggleSelect={onToggleSelect}
                    onRemove={onRemoveCard}
                    position={positionMap.get(product.id) ?? 0}
                    isAddedFromLowerTier={addedIds.has(product.id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          {editHasMore && (
            <div ref={editSentinelRef} style={{ height: "1px" }} aria-hidden="true" />
          )}
        </>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-6 pt-4">
            {renderedDisplayProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onClick={() => onProductClick(product.id)}
                isAddedFromLowerTier={addedIds.has(product.id)}
              />
            ))}
          </div>
          {viewHasMore && (
            <div ref={viewSentinelRef} style={{ height: "1px" }} aria-hidden="true" />
          )}
        </>
      )}
    </Box>
  );
}
